import React, { useState } from 'react';
import PropTypes from 'prop-types';
import useBeep from '@afosto/utils-react/useBeep';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useScanner from '@afosto/utils-react/input/useScanner';
import { useConfirm } from '@afosto/app-components-react/ConfirmDialog';
import { useDataSource } from '@afosto/app-components-react/DataSource';
import { useSnackbar } from 'notistack';
import PackingProvider from '../PackingProvider';
import Box from '@material-ui/core/Box';
import Hidden from '@material-ui/core/Hidden';
import FinishButton from '../FinishButton';
import ItemResultsDialog from '../ItemResultsDialog';
import ItemQuantityDialog from '../ItemQuantityDialog';
import MobileActionBar from '../MobileActionBar';
import PackingList from './PackingList';
import PackingItemParcels from './PackingItemParcels';
import PackingMaxQuantityDialog from './PackingMaxQuantityDialog';
import PackingMobileContainer from './PackingMobileContainer';
import PackingParcelsDialog from './PackingParcelsDialog';
import ProductNotFoundDialog from '../ProductNotFoundDialog';
import BulkButton from '@afosto/components-react/BulkButton';
import ProgressDialog from '@afosto/app-components-react/ProgressDialog';
import dataSourceRequest from '@afosto/utils-react/dataSource/dataSourceRequest';
import fetchMultipleProducts from '@afosto/utils-react/fetchMultipleProducts';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import { bulk as bulkApi } from '@afosto/fulfilment/api/Bulk';
import { parcels as parcelsApi } from '@afosto/fulfilment/api/Parcels';
import { search as searchApi } from '@afosto/fulfilment/api/Search';
import translations from './translations';

const DEFAULT_ITEM_STATUS_CONSTRAINTS = [
  {
    key: 'is_boxed',
    operator: 'in',
    value: false,
  },
  {
    key: 'is_finished',
    operator: 'in',
    value: false,
  },
  {
    key: 'is_missing',
    operator: 'in',
    value: false,
  },
];

const Packing = ({ onFinish, packingContext }) => {
  const confirm = useConfirm();
  const intl = useIntl();
  const playBeep = useBeep();
  const { enqueueSnackbar } = useSnackbar();
  const { shipment, currentParcel } = packingContext ?? {};
  const isOutbound = shipment?.is_outbound ?? true;
  const [activeItem, setActiveItem] = useState(null);
  const [isPackingItems, setIsPackingItems] = useState(false);
  const [itemResults, setItemResults] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showItemResultsDialog, setShowItemsResultsDialog] = useState(false);
  const [showPackingMaxQuantityDialog, setShowPackingMaxQuantityDialog] = useState(false);
  const [showPackingParcelsDialog, setShowPackingParcelsDialog] = useState(false);
  const [showProductNotFoundDialog, setShowProductNotFoundDialog] = useState(false);

  const fetchItems = async dataSource =>
    dataSourceRequest(
      dataSource,
      async (data, requestOptions) =>
        searchApi.listListItems(
          {
            constraints: [
              {
                key: 'shipment_ids',
                operator: 'in',
                value: [shipment?.id],
              },
              ...DEFAULT_ITEM_STATUS_CONSTRAINTS,
            ],
          },
          requestOptions,
        ),
      {
        transformData: async (data, source) => {
          const { pagination = {} } = source ?? {};
          const { itemsPerPage } = pagination;
          const skus = data.map(({ sku }) => sku);
          const products = await fetchMultipleProducts(
            skus,
            {},
            {
              headers: {
                'x-page-size': itemsPerPage,
              },
            },
          );
          const mappedProducts = products.reduce(
            (acc, product) => ({ ...acc, [product?.sku]: product }),
            {},
          );

          return data.reduce((acc, item) => {
            const [firstId] = item?.ids ?? [];
            const quantity = item.quantity - (item?.boxed_ids?.length ?? 0);

            const itemParcels = (item?.parcels ?? []).reduce(
              (parcelAcc, parcel) => ({ ...parcelAcc, [parcel.id]: parcel }),
              {},
            );

            return [
              ...acc,
              {
                ...item,
                id: firstId,
                product: mappedProducts[item.sku],
                parcels: itemParcels,
                quantity,
              },
            ];
          }, []);
        },
      },
    );

  const dataSource = useDataSource(fetchItems, {
    modules: ['pagination', 'selection'],
    pagination: {
      itemsPerPage: 10,
    },
    selection: {
      onAddSelection: selection => {
        setSelectedItems(oldItems => {
          const currentItemIds = oldItems.map(item => item.id);
          const newItems = selection.filter(item => !currentItemIds.includes(item.id));

          return [...oldItems, ...newItems];
        });
      },
      onRemoveSelection: selection => {
        const removedIds = selection.map(item => item.id);
        setSelectedItems(oldItems => oldItems.filter(item => !removedIds.includes(item.id)));
      },
    },
  });

  const handleSelectItemResult = selectedItem => {
    setActiveItem(selectedItem);
    setItemResults([]);
    setShowItemsResultsDialog(false);
  };

  const handleScanInput = async value => {
    try {
      const products = await fetchMultipleProducts([value]);
      const filteredProducts = products.filter(product => product.ean === value);

      if (filteredProducts.length) {
        const skus = filteredProducts.map(product => product.sku);
        const response = await searchApi.listListItems({
          constraints: [
            {
              key: 'shipment_ids',
              operator: 'in',
              value: [shipment?.id],
            },
            {
              key: 'skus',
              operator: 'in',
              value: skus,
            },
            ...DEFAULT_ITEM_STATUS_CONSTRAINTS,
          ],
        });
        const { data: items = [] } = response ?? {};

        if (items?.length >= 1 && filteredProducts?.length > 1) {
          const formattedResults = items.reduce((acc, item) => {
            const product = findObjectInArray(filteredProducts, 'sku', item?.sku);
            return [...acc, { ...item, product }];
          }, []);
          setItemResults(formattedResults);
          setShowItemsResultsDialog(true);
          return;
        }

        if (items?.length > 0) {
          const [firstItem] = items;
          const product = findObjectInArray(filteredProducts, 'sku', firstItem?.sku);
          setActiveItem({ ...firstItem, product });
          return;
        }
      }

      if (value) {
        playBeep('error');
        setShowProductNotFoundDialog(true);
      }
    } catch (error) {
      // Do nothing
    }
  };

  useScanner(handleScanInput, { disabled: !!activeItem });

  const handleClickItem = (event, item) => {
    setActiveItem(item);
  };

  const handleCloseItemDialog = () => {
    setActiveItem(null);
    dataSource.reloadDataSource();
  };

  const handleCloseItemResultsDialog = () => {
    setShowItemsResultsDialog(false);
  };

  const handleClosePackingParcelsDialog = () => {
    setShowPackingParcelsDialog(false);
  };

  const handleCloseProductNotFoundDialog = () => {
    setShowProductNotFoundDialog(false);
  };

  const handleMaxQuantityReached = () => {
    setShowPackingMaxQuantityDialog(true);
  };

  const handleCloseMaxQuantityDialog = () => {
    setShowPackingMaxQuantityDialog(false);
  };

  const handleSaveCurrentItem = async quantity => {
    try {
      const availableIds = (activeItem?.ids ?? []).filter(
        id => !activeItem?.boxed_ids?.includes(id),
      );
      const ids = availableIds.slice(0, Math.abs(quantity));

      if (ids.length > 0) {
        await parcelsApi.addToParcel(currentParcel?.id, {
          ids,
        });

        const newQuantity = availableIds?.length - ids?.length;

        if (newQuantity >= 0) {
          const itemParcels = activeItem?.parcels ?? {};
          const currentItemParcel = itemParcels[currentParcel?.id];

          setActiveItem({
            ...activeItem,
            boxed_ids: [...(activeItem?.boxed_ids ?? []), ...ids],
            parcels: {
              ...itemParcels,
              [currentParcel?.id]: {
                ...(currentItemParcel ?? {}),
                item_ids: [...(currentItemParcel?.item_ids ?? []), ...ids],
              },
            },
            quantity: newQuantity,
          });
        }
      }
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.addItemsToParcelFailed), {
        variant: 'error',
      });
    }
  };

  const handleSave = async ({ quantity }) => {
    if (quantity > 0) {
      await handleSaveCurrentItem(quantity);
    }

    handleCloseItemDialog();
  };

  const handleBulkAddToParcel = async parcel =>
    bulkApi.bulkUpdateItems({
      command: 'set_parameters',
      parameters: {
        parcel_id: parcel?.id,
      },
      constraints: [
        {
          key: 'shipment_ids',
          operator: 'in',
          value: [shipment?.id],
        },
        ...DEFAULT_ITEM_STATUS_CONSTRAINTS,
      ],
    });

  const handlePackMultipleItems = async parcel => {
    try {
      setIsPackingItems(true);
      setShowPackingParcelsDialog(false);

      const allItemsSelected = dataSource.selection.allItemsSelected ?? false;
      const itemIds = selectedItems.reduce((acc, item) => [...acc, ...(item?.ids ?? [])], []);

      if (!allItemsSelected && itemIds.length === 0) {
        return;
      }

      if (!allItemsSelected && itemIds.length > 0) {
        await parcelsApi.addToParcel(parcel?.id, {
          ids: itemIds,
        });
      }

      if (allItemsSelected) {
        await handleBulkAddToParcel(parcel);
      }

      enqueueSnackbar(
        intl.formatMessage(
          translations.feedback[isOutbound ? 'multipleItemsPacked' : 'multipleItemsUnpacked'],
        ),
        {
          variant: 'success',
        },
      );

      setSelectedItems([]);
      dataSource.selection.setAllItemsSelected(false);
      dataSource.selection.setSelectedItems([]);
      dataSource.pagination.setActivePage(0);
      dataSource.pagination.setPageCursor(null);
      dataSource.reloadDataSource();
    } catch (err) {
      playBeep('error');
      enqueueSnackbar(
        intl.formatMessage(
          translations.feedback[isOutbound ? 'multipleItemsNotPacked' : 'multipleItemsNotUnpacked'],
        ),
        {
          variant: 'error',
        },
      );
    } finally {
      setIsPackingItems(false);
    }
  };

  const finishPacking = () => {
    if (onFinish && typeof onFinish === 'function') {
      onFinish();
    }
  };

  const reportMissingItems = async () =>
    bulkApi.bulkUpdateItems({
      command: 'set_parameters',
      parameters: {
        is_missing: true,
      },
      constraints: [
        {
          key: 'shipment_ids',
          operator: 'in',
          value: [shipment?.id],
        },
        ...DEFAULT_ITEM_STATUS_CONSTRAINTS,
      ],
    });

  const handleRemainingItems = async () => {
    try {
      const response = await confirm({
        title: intl.formatMessage(translations.remainingItemsDialog.title),
        description: intl.formatMessage(
          translations.remainingItemsDialog[
            isOutbound ? 'descriptionPacking' : 'descriptionUnpacking'
          ],
        ),
        disableBackdropClick: true,
        catchOnCancel: false,
        buttons: [
          {
            label: intl.formatMessage(translations.remainingItemsDialog.buttons.cancel),
            type: 'cancel',
            variant: 'outlined',
            style: {
              marginRight: 'auto',
            },
          },
          {
            label: intl.formatMessage(translations.remainingItemsDialog.buttons.finish),
            type: 'submit',
            response: 'force-finish',
            variant: 'contained',
            color: 'primary',
          },
        ],
      });

      if (response === 'cancel') {
        return;
      }

      await reportMissingItems();

      finishPacking();
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.reportMissingItemsRequestFailed), {
        variant: 'error',
      });
    }
  };

  const handleFinish = async () => {
    if (dataSource?.data?.length > 0) {
      await handleRemainingItems();
      return;
    }

    finishPacking();
  };

  const bulkActions = [
    {
      key: 'multiPacking',
      render: () => (
        <BulkButton
          key="multiPacking"
          variant="outlined"
          size="small"
          onClick={() => setShowPackingParcelsDialog(true)}
        >
          {intl.formatMessage(translations.bulkActions[isOutbound ? 'packing' : 'unpacking'])}
        </BulkButton>
      ),
    },
  ];

  return (
    <PackingProvider value={{ ...packingContext, handleSaveCurrentItem }}>
      <Hidden mdUp implementation="js">
        <PackingMobileContainer>
          <PackingList
            bulkActions={bulkActions}
            dataSource={dataSource}
            onClick={handleClickItem}
          />
          <MobileActionBar>
            <FinishButton onClick={handleFinish} style={{ marginLeft: 'auto' }} />
          </MobileActionBar>
        </PackingMobileContainer>
      </Hidden>
      <Hidden smDown implementation="js">
        <PackingList bulkActions={bulkActions} dataSource={dataSource} onClick={handleClickItem} />
        <Box pt={3} textAlign="right">
          <FinishButton onClick={handleFinish} />
        </Box>
      </Hidden>
      <ItemResultsDialog
        isOpen={showItemResultsDialog}
        onClose={handleCloseItemResultsDialog}
        onSelect={handleSelectItemResult}
        results={itemResults}
      />
      <ItemQuantityDialog
        title={intl.formatMessage(
          translations.itemQuantityDialog[isOutbound ? 'packTitle' : 'unpackTitle'],
        )}
        isOpen={!!activeItem}
        item={activeItem}
        onClose={handleCloseItemDialog}
        onMaxQuantityReached={handleMaxQuantityReached}
        onSave={handleSave}
        onBeforeSwitchParcel={({ quantity }) => handleSaveCurrentItem(quantity)}
        PackingComponent={PackingItemParcels}
        showPosition={false}
        quantityCounterProps={{
          requestedLabel: intl.formatMessage(
            translations.itemQuantityDialog.quantityCounter.quantity,
          ),
          quantityLabel: intl.formatMessage(
            translations.itemQuantityDialog.quantityCounter[isOutbound ? 'pack' : 'unpack'],
          ),
        }}
        allowMoreThanRequested={false}
        alwaysEnableSaveButton
      />
      <PackingMaxQuantityDialog
        isOpen={showPackingMaxQuantityDialog}
        isOutbound={isOutbound}
        onClose={handleCloseMaxQuantityDialog}
      />
      <PackingParcelsDialog
        isOpen={showPackingParcelsDialog}
        onClose={handleClosePackingParcelsDialog}
        onSelect={handlePackMultipleItems}
      />
      <ProgressDialog
        isOpen={isPackingItems}
        message={intl.formatMessage(
          translations.progressDialog[isOutbound ? 'packingMessage' : 'unpackingMessage'],
        )}
      />
      <ProductNotFoundDialog
        isOpen={showProductNotFoundDialog}
        onClose={handleCloseProductNotFoundDialog}
      />
    </PackingProvider>
  );
};

Packing.propTypes = {
  onFinish: PropTypes.func,
  packingContext: PropTypes.object.isRequired,
};

Packing.defaultProps = {
  onFinish: undefined,
};

export default Packing;
